import LegalLayout, {
  LegalSection,
  LegalSubhead,
  LegalList,
  LegalParagraph,
  LegalCallout,
} from './LegalLayout';

/**
 * Privacy Policy — Google-compliant version supplied by the operator.
 * Content is rendered verbatim per the source text; only structure +
 * presentation are styled so the page reads cleanly on mobile and
 * desktop alike. Effective Date and contact email match the source.
 */
export default function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" effectiveDate="April 26, 2026">
      <LegalSection number={1} title="Introduction">
        <LegalParagraph>
          VegasLobby (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;) operates a digital
          entertainment platform designed to provide interactive user experiences and
          account-based services. This Privacy Policy explains how we collect, use, and
          protect your information.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={2} title="Information We Collect">
        <LegalSubhead>a. Information from Google Sign-In</LegalSubhead>
        <LegalParagraph>When you use Google to sign in, we collect:</LegalParagraph>
        <LegalList items={['Name', 'Email address', 'Google account identifier']} />

        <LegalParagraph>We do NOT access:</LegalParagraph>
        <LegalList variant="cross" items={['Contacts', 'Google Drive', 'Any sensitive Google data']} />

        <LegalSubhead>b. Account Information</LegalSubhead>
        <LegalList items={['Username', 'Login credentials (secured and encrypted)']} />

        <LegalSubhead>c. Usage Information</LegalSubhead>
        <LegalList items={['Login timestamps', 'Platform interactions', 'Device/browser type', 'IP address']} />

        <LegalSubhead>d. Transaction & Activity Data</LegalSubhead>
        <LegalList items={['Virtual credit activity', 'Account balance interactions', 'System logs for auditing and security']} />
      </LegalSection>

      <LegalSection number={3} title="How We Use Your Information">
        <LegalParagraph>We use collected data to:</LegalParagraph>
        <LegalList
          items={[
            'Create and manage user accounts',
            'Authenticate users securely',
            'Provide access to platform features',
            'Maintain account balances and activity logs',
            'Detect fraud, abuse, or unauthorized access',
            'Improve system performance and reliability',
          ]}
        />
      </LegalSection>

      <LegalSection number={4} title="Google User Data Compliance">
        <LegalParagraph>VegasLobby complies with Google API Services User Data Policy:</LegalParagraph>
        <LegalList
          items={[
            'We only use Google user data for authentication purposes',
            'We do NOT sell or share Google user data',
            'We do NOT use Google data for advertising',
            'We do NOT access data beyond approved scopes',
          ]}
        />
      </LegalSection>

      <LegalSection number={5} title="Data Sharing">
        <LegalParagraph>We may share data only with:</LegalParagraph>
        <LegalList
          items={[
            'Infrastructure providers (hosting, security)',
            'Authentication providers (Google)',
            'Legal authorities (if required by law)',
          ]}
        />
        <LegalCallout>We do NOT sell personal data.</LegalCallout>
      </LegalSection>

      <LegalSection number={6} title="Data Security">
        <LegalParagraph>We implement:</LegalParagraph>
        <LegalList
          items={[
            'HTTPS encryption',
            'Secure authentication mechanisms',
            'Access control systems',
            'Monitoring and logging',
          ]}
        />
      </LegalSection>

      <LegalSection number={7} title="Data Retention">
        <LegalParagraph>We retain data:</LegalParagraph>
        <LegalList
          items={[
            'While your account is active',
            'As required for security, compliance, and operational purposes',
          ]}
        />
        <LegalParagraph>Users may request deletion of their data.</LegalParagraph>
      </LegalSection>

      <LegalSection number={8} title="User Rights">
        <LegalParagraph>You may:</LegalParagraph>
        <LegalList
          items={[
            'Request access to your data',
            'Request correction',
            'Request deletion',
          ]}
        />
        <LegalParagraph>
          Contact us at:{' '}
          <a
            href="mailto:privacy@vegaslobby.com"
            className="text-[#D4AF37] hover:underline"
            data-testid="privacy-contact-email"
          >
            privacy@vegaslobby.com
          </a>
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={9} title="Cookies">
        <LegalParagraph>We use cookies for:</LegalParagraph>
        <LegalList items={['Session management', 'Authentication', 'Platform functionality']} />
      </LegalSection>

      <LegalSection number={10} title="Children's Privacy">
        <LegalCallout>This platform is intended for users 18+ only.</LegalCallout>
      </LegalSection>

      <LegalSection number={11} title="Changes">
        <LegalParagraph>We may update this policy. Continued use means acceptance.</LegalParagraph>
      </LegalSection>

      <LegalSection number={12} title="Contact">
        <LegalParagraph>
          <a
            href="mailto:privacy@vegaslobby.com"
            className="text-[#D4AF37] hover:underline"
          >
            privacy@vegaslobby.com
          </a>
        </LegalParagraph>
      </LegalSection>
    </LegalLayout>
  );
}
